/**
 * Firebase Functions API service
 * Provides methods to call deployed Firebase Functions
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../firebase-config';

// Initialize Functions
const functions = getFunctions(app, 'northamerica-northeast1');

// Create callable function references
const createCallable = (name) => httpsCallable(functions, name);

// System Management
export const initializeSystem = createCallable('initializeSystem');
export const createFirstAdmin = createCallable('createFirstAdmin');

// Company Management
export const createCompany = createCallable('createCompany');
export const updateCompany = createCallable('updateCompany');
export const deleteCompany = createCallable('deleteCompany');
export const listCompanies = createCallable('listCompanies');
export const getCompany = createCallable('getCompany');

// Role Management
export const createRole = createCallable('createRole');
export const updateRole = createCallable('updateRole');
export const deleteRole = createCallable('deleteRole');
export const listRoles = createCallable('listRoles');
export const assignRolesToUser = createCallable('assignRolesToUser');

// Service Management
export const registerService = createCallable('registerService');
export const updateService = createCallable('updateService');
export const deleteService = createCallable('deleteService');
export const listServices = createCallable('listServices');
export const getServiceHealth = createCallable('getServiceHealth');

// User Management
export const createUser = createCallable('createUser');
export const sendWelcomeEmail = createCallable('sendWelcomeEmail');
export const refreshUserClaims = createCallable('refreshUserClaims');
export const listUsers = createCallable('listUsers');
export const listUnassignedUsers = createCallable('listUnassignedUsers');
export const deleteUser = createCallable('deleteUser');
export const updateUserStatus = createCallable('updateUserStatus');
export const updateUser = createCallable('updateUser');

// Email
export const resendInvitationEmail = createCallable('resendInvitationEmail');

// Signup Requests
export const approveSignupRequest = createCallable('approveSignupRequest');
export const rejectSignupRequest = createCallable('rejectSignupRequest');

// API wrapper functions with error handling
export const api = {
  // System
  async initializeSystem() {
    try {
      const result = await initializeSystem({});
      return result.data;
    } catch (error) {
      console.error('Initialize system error:', error);
      throw new Error(error.message || 'Failed to initialize system');
    }
  },

  async createFirstAdmin(email, company = 'sofiapulse') {
    try {
      const result = await createFirstAdmin({ email, company });
      return result.data;
    } catch (error) {
      console.error('Create first admin error:', error);
      throw new Error(error.message || 'Failed to create first admin');
    }
  },

  // Companies
  async listCompanies() {
    try {
      const result = await listCompanies({});
      return result.data.companies || [];
    } catch (error) {
      console.error('List companies error:', error);
      throw new Error(error.message || 'Failed to load companies');
    }
  },

  async createCompany(companyData) {
    try {
      const result = await createCompany(companyData);
      return result.data;
    } catch (error) {
      console.error('Create company error:', error);
      throw new Error(error.message || 'Failed to create company');
    }
  },

  async updateCompany(company, updates) {
    try {
      const result = await updateCompany({ company, updates });
      return result.data;
    } catch (error) {
      console.error('Update company error:', error);
      throw new Error(error.message || 'Failed to update company');
    }
  },

  async deleteCompany(company) {
    try {
      const result = await deleteCompany({ company });
      return result.data;
    } catch (error) {
      console.error('Delete company error:', error);
      throw new Error(error.message || 'Failed to delete company');
    }
  },

  // Roles
  async listRoles(companyType = null) {
    try {
      const result = await listRoles({ company_type: companyType });
      return result.data.roles || [];
    } catch (error) {
      console.error('List roles error:', error);
      throw new Error(error.message || 'Failed to load roles');
    }
  },

  async createRole(roleData) {
    try {
      const result = await createRole(roleData);
      return result.data;
    } catch (error) {
      console.error('Create role error:', error);
      throw new Error(error.message || 'Failed to create role');
    }
  },

  async updateRole(roleId, updates) {
    try {
      const result = await updateRole({ roleId, updates });
      return result.data;
    } catch (error) {
      console.error('Update role error:', error);
      throw new Error(error.message || 'Failed to update role');
    }
  },

  async deleteRole(roleId) {
    try {
      const result = await deleteRole({ roleId });
      return result.data;
    } catch (error) {
      console.error('Delete role error:', error);
      throw new Error(error.message || 'Failed to delete role');
    }
  },

  // Services
  async listServices(status = null) {
    try {
      const result = await listServices({ status });
      return result.data.services || [];
    } catch (error) {
      console.error('List services error:', error);
      throw new Error(error.message || 'Failed to load services');
    }
  },

  async registerService(serviceData) {
    try {
      const result = await registerService(serviceData);
      return result.data;
    } catch (error) {
      console.error('Register service error:', error);
      throw new Error(error.message || 'Failed to register service');
    }
  },

  async updateService(serviceId, updates) {
    try {
      const result = await updateService({ serviceId, updates });
      return result.data;
    } catch (error) {
      console.error('Update service error:', error);
      throw new Error(error.message || 'Failed to update service');
    }
  },

  async deleteService(serviceId) {
    try {
      const result = await deleteService({ serviceId });
      return result.data;
    } catch (error) {
      console.error('Delete service error:', error);
      throw new Error(error.message || 'Failed to delete service');
    }
  },

  // Users
  async assignRolesToUser(userId, roles) {
    try {
      const result = await assignRolesToUser({ userId, roles });
      return result.data;
    } catch (error) {
      console.error('Assign roles error:', error);
      throw new Error(error.message || 'Failed to assign roles');
    }
  },

  async refreshUserClaims(userId = null) {
    try {
      const result = await refreshUserClaims({ userId });
      return result.data;
    } catch (error) {
      console.error('Refresh claims error:', error);
      throw new Error(error.message || 'Failed to refresh user claims');
    }
  },

  async listUsers() {
    try {
      const result = await listUsers({});
      return result.data.users || [];
    } catch (error) {
      console.error('List users error:', error);
      throw new Error(error.message || 'Failed to load users');
    }
  },

  async listUnassignedUsers() {
    try {
      const result = await listUnassignedUsers({});
      return result.data.unassignedUsers || [];
    } catch (error) {
      console.error('List unassigned users error:', error);
      throw new Error(error.message || 'Failed to load unassigned users');
    }
  },

  async resendInvitationEmail(invitationId) {
    try {
      const result = await resendInvitationEmail({ invitationId });
      return result.data;
    } catch (error) {
      console.error('Resend invitation error:', error);
      throw new Error(error.message || 'Failed to resend invitation');
    }
  },

  async deleteUser(userId) {
    try {
      const result = await deleteUser({ userId });
      return result.data;
    } catch (error) {
      console.error('Delete user error:', error);
      throw new Error(error.message || 'Failed to delete user');
    }
  },

  async updateUserStatus(userId, status) {
    try {
      const result = await updateUserStatus({ userId, status });
      return result.data;
    } catch (error) {
      console.error('Update user status error:', error);
      throw new Error(error.message || 'Failed to update user status');
    }
  },

  async updateUser(userId, updates) {
    try {
      const result = await updateUser({ userId, updates });
      return result.data;
    } catch (error) {
      console.error('Update user error:', error);
      throw new Error(error.message || 'Failed to update user');
    }
  },

  async createUser(userData) {
    try {
      const result = await createUser(userData);
      return result.data;
    } catch (error) {
      console.error('Create user error:', error);
      throw new Error(error.message || 'Failed to create user');
    }
  },

  async sendWelcomeEmail(userId) {
    try {
      const result = await sendWelcomeEmail({ userId });
      return result.data;
    } catch (error) {
      console.error('Send welcome email error:', error);
      throw new Error(error.message || 'Failed to send welcome email');
    }
  },

  // Signup Requests
  async approveSignupRequest(payload) {
    try {
      const result = await approveSignupRequest(payload);
      return result.data;
    } catch (error) {
      console.error('Approve signup request error:', error);
      throw new Error(error.message || 'Failed to approve signup request');
    }
  },

  async rejectSignupRequest(requestId, reason = null) {
    try {
      const result = await rejectSignupRequest({ requestId, reason });
      return result.data;
    } catch (error) {
      console.error('Reject signup request error:', error);
      throw new Error(error.message || 'Failed to reject signup request');
    }
  },
};
